import React, { useCallback, useContext, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Form, Spinner } from 'react-bootstrap';
import { PencilFill } from 'react-bootstrap-icons';
import { Formik } from 'formik';
import * as yup from 'yup';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import ProductSearchMultiselect from '../Generic/ProductSearchMultiselect';
import RequiredAsteriskComponent from '../Generic/RequiredAsteriskComponent';
import {
  createProtocol,
  getProtocolDetails,
  updateProtocol,
} from '../../clients/protocol_client';
import {
  getProtocolErrorMessage,
  getProtocolNameError,
  toProductOptions,
} from '../../utils/protocols';
import { showToast } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';
import '../Glass/ProcureModalStyle.css';
import '../Glass/GlassStyle.css';

const LOG_CONTEXT = 'ProtocolModal';

const URL_MAX_LENGTH = 500;

/**
 * Validation schema for the protocol form.
 *
 * `name` is required and unique per lab, but uniqueness has no pre-submit
 * endpoint — it is reported by the backend as a 400 and rendered inline.
 * `description` and `url` are optional; `url` must be a valid URL of at most
 * 500 characters.
 */
const formSchema = yup.object().shape({
  name: yup.string().trim().required('Protocol name is required'),
  description: yup.string(),
  url: yup
    .string()
    .url('Enter a valid URL')
    .max(URL_MAX_LENGTH, `Link must be at most ${URL_MAX_LENGTH} characters`),
});

/**
 * True when a payload carries every editable field.
 *
 * The protocol stubs on product detail are only `{ id, name, url }`, so
 * submitting them straight from the form would blank `description` and drop
 * every product link.
 *
 * @param {Object|null|undefined} protocol
 * @returns {boolean}
 */
function isCompleteProtocolPayload(protocol) {
  return Boolean(
    protocol &&
      Array.isArray(protocol.products) &&
      Object.prototype.hasOwnProperty.call(protocol, 'description')
  );
}

/**
 * Create/edit modal for a lab protocol, including its linked products.
 *
 * Doubles as the protocol detail view: the Protocols table opens it from the
 * name cell, and the product detail section opens it from a protocol row.
 *
 * @param {Object} props
 * @param {Object} [props.protocolObj] - Existing protocol; omit to create.
 * @param {Function} [props.onSuccessfulSubmit] - Called after create/update succeeds.
 * @param {Function} [props.clearSearchValue] - Clears the parent list search after create.
 * @param {Function} [props.onEditButtonHover] - Prefetch hook for the parent (hydrates `products[]`).
 * @param {boolean} [props.isEditButtonLoading] - Show a spinner while the prefetch runs.
 * @param {React.ReactNode} [props.triggerContent] - Custom trigger label (e.g. the protocol name).
 * @param {string} [props.triggerVariant] - Bootstrap variant for the trigger button.
 * @param {string} [props.triggerClassName] - Extra classes for the trigger button.
 */
const ProtocolModal = ({
  protocolObj,
  onSuccessfulSubmit,
  clearSearchValue,
  onEditButtonHover,
  isEditButtonLoading = false,
  triggerContent = null,
  triggerVariant = null,
  triggerClassName = '',
}) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameServerError, setNameServerError] = useState('');

  // The payload actually rendered by the form. Starts as whatever the parent
  // passed and is replaced once the full protocol has been fetched.
  const [protocolData, setProtocolData] = useState(protocolObj ?? null);

  // Linked products live outside Formik: the picker is an option-object list,
  // and whether it was touched decides if `product_ids` is sent at all.
  const [relatedProducts, setRelatedProducts] = useState(
    toProductOptions(protocolObj?.products)
  );
  const [productsTouched, setProductsTouched] = useState(false);
  const [isHydrating, setIsHydrating] = useState(false);

  const isEditing = Boolean(protocolObj);

  // Resync when the parent swaps in a hydrated payload (e.g. hover prefetch).
  useEffect(() => {
    setProtocolData(protocolObj ?? null);
    setRelatedProducts(toProductOptions(protocolObj?.products));
    setProductsTouched(false);
  }, [protocolObj]);

  /**
   * Fetch the full protocol when the modal was opened from a partial payload,
   * such as the `{ id, name, url }` stubs on product detail.
   */
  const hydrateProtocol = useCallback(async () => {
    if (!protocolObj?.id || isCompleteProtocolPayload(protocolObj)) return;

    setIsHydrating(true);
    const result = await getProtocolDetails(token, protocolObj.id);
    setIsHydrating(false);

    if (result?.success) {
      setProtocolData(result.data);
      setRelatedProducts(toProductOptions(result.data?.products));
      setProductsTouched(false);
    } else {
      logError(LOG_CONTEXT, 'failed to hydrate protocol', {
        protocolId: protocolObj.id,
        status: result?.status,
      });
      showToast(
        getProtocolErrorMessage(result, 'Could not load the protocol.'),
        'error',
        'top-right',
        4000
      );
      // Without the full payload the form would submit partial data, so there
      // is nothing safe to show.
      setShowModal(false);
    }
  }, [token, protocolObj]);

  useEffect(() => {
    if (showModal) {
      hydrateProtocol();
    }
  }, [showModal, hydrateProtocol]);

  // Editing must not submit a partial payload: blanking `description` or
  // replacing `product_ids` from an unhydrated stub would lose data.
  const isFormReady = !isEditing || isCompleteProtocolPayload(protocolData);

  const handleShow = () => setShowModal(true);

  const handleClose = () => {
    setShowModal(false);
    setNameServerError('');
    if (!isEditing) {
      setRelatedProducts([]);
      setProductsTouched(false);
    }
  };

  /**
   * Build the request body. `product_ids` replaces the whole link set, so it is
   * only sent when the user actually changed the selection (or is creating).
   *
   * @param {Object} values - Formik values
   * @returns {Object}
   */
  const buildProtocolPayload = values => {
    const payload = {
      name: values.name.trim(),
      description: values.description ?? '',
      url: values.url ? values.url.trim() : null,
    };

    if (!isEditing || productsTouched) {
      payload.product_ids = relatedProducts.map(option => option.value);
    }

    return payload;
  };

  function handleSubmit(values) {
    setIsSubmitting(true);
    setNameServerError('');

    const payload = buildProtocolPayload(values);

    logInfo(LOG_CONTEXT, 'submit protocol', {
      protocolId: protocolObj?.id ?? null,
      isEditing,
      sendsProductIds: Object.prototype.hasOwnProperty.call(
        payload,
        'product_ids'
      ),
      productIdCount: payload.product_ids?.length ?? null,
    });

    const protocolPromise = isEditing
      ? updateProtocol(token, protocolObj.id, payload)
      : createProtocol(token, payload);

    protocolPromise.then(response => {
      setIsSubmitting(false);

      if (response?.success) {
        if (onSuccessfulSubmit) {
          onSuccessfulSubmit(response.updatedProtocol ?? response.protocol);
        }
        if (!isEditing && clearSearchValue) {
          clearSearchValue();
        }
        response.toast();
        handleClose();
        return;
      }

      const nameError = getProtocolNameError(response?.fieldErrors);
      if (nameError) {
        setNameServerError(nameError);
        return;
      }

      showToast(
        getProtocolErrorMessage(response),
        'error',
        'top-right',
        4000
      );
    });
  }

  const renderTrigger = () => {
    if (triggerContent) {
      return (
        <Button
          variant={triggerVariant ?? 'link'}
          className={triggerClassName}
          onClick={handleShow}
          onMouseEnter={onEditButtonHover}
        >
          {triggerContent}
        </Button>
      );
    }

    return (
      <Button
        variant={
          triggerVariant ?? (isEditing ? 'outline-success' : 'success')
        }
        className={triggerClassName}
        onClick={handleShow}
        onMouseEnter={onEditButtonHover}
      >
        {isEditing ? <PencilFill /> : 'Create Protocol'}
      </Button>
    );
  };

  if (isViewer && !isEditing) {
    return null;
  }

  return (
    <>
      {renderTrigger()}

      <Modal
        show={showModal}
        onHide={handleClose}
        backdrop="static"
        dialogClassName="procure-modal-glass procure-modal-glass-form"
      >
        <Modal.Header closeButton className="procure-modal__header">
          <Modal.Title>
            {isViewer && isEditing
              ? protocolObj.name || `Protocol #${protocolObj.id}`
              : isEditing
                ? `Edit Protocol ${protocolObj.name || `#${protocolObj.id}`}`
                : 'Create Protocol'}
          </Modal.Title>
        </Modal.Header>

        {isViewer && isEditing ? (
          <>
            <Modal.Body className="procure-modal__body">
              {isHydrating ? (
                <div className="text-center py-4">
                  <Spinner animation="border" className="mb-3" />
                  <div className="text-muted">Loading protocol details...</div>
                </div>
              ) : (
                <>
                  <p className="mb-2">
                    <strong>Name</strong>
                    <br />
                    {protocolData?.name || '—'}
                  </p>
                  <p className="mb-2">
                    <strong>Description</strong>
                    <br />
                    {protocolData?.description || '—'}
                  </p>
                  <p className="mb-2">
                    <strong>External link</strong>
                    <br />
                    {protocolData?.url ? (
                      <a
                        href={protocolData.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {protocolData.url}
                      </a>
                    ) : (
                      '—'
                    )}
                  </p>
                  <p className="mb-1">
                    <strong>Linked products</strong>
                  </p>
                  {relatedProducts.length ? (
                    <ul className="mb-0">
                      {relatedProducts.map(product => (
                        <li key={product.value}>{product.label}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted mb-0">None linked.</p>
                  )}
                </>
              )}
            </Modal.Body>
            <Modal.Footer className="procure-modal__footer">
              <Button
                variant="secondary"
                className="procure-modal__close-btn"
                onClick={handleClose}
              >
                Close
              </Button>
            </Modal.Footer>
          </>
        ) : isEditing && (isEditButtonLoading || isHydrating || !isFormReady) ? (
          <Modal.Body
            className="procure-modal__body d-flex justify-content-center align-items-center"
            style={{ minHeight: '200px' }}
          >
            <div className="text-center">
              <Spinner animation="border" size="lg" className="mb-3" />
              <div className="text-muted">Loading protocol details...</div>
            </div>
          </Modal.Body>
        ) : (
          <Formik
            initialTouched={
              isEditing ? { name: true, description: true, url: true } : {}
            }
            initialValues={{
              name: protocolData?.name ?? '',
              description: protocolData?.description ?? '',
              url: protocolData?.url ?? '',
            }}
            validateOnMount={isEditing}
            enableReinitialize={true}
            validationSchema={formSchema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({
              handleChange,
              handleSubmit,
              values,
              handleBlur,
              touched,
              errors,
              isValid,
              dirty,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Modal.Body className="procure-modal__body d-flex flex-column">
                  <Form.Group controlId="formProtocolName" className="mb-3">
                    <Form.Label>
                      Protocol Name {<RequiredAsteriskComponent />}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={values.name}
                      onChange={event => {
                        // The duplicate-name error came from the last save; a
                        // new keystroke makes it stale.
                        setNameServerError('');
                        handleChange(event);
                      }}
                      onBlur={handleBlur}
                      isInvalid={
                        (touched.name && !!errors.name) || !!nameServerError
                      }
                      isValid={
                        touched.name &&
                        !errors.name &&
                        !nameServerError &&
                        !!values.name
                      }
                    />
                    <Form.Control.Feedback type="valid">
                      Looks good!
                    </Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                      {errors.name || nameServerError}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Protocol names must be unique within your lab.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group
                    controlId="formProtocolDescription"
                    className="mb-3"
                  >
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.description && !!errors.description}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.description}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group controlId="formProtocolUrl" className="mb-4">
                    <Form.Label>External Link</Form.Label>
                    <Form.Control
                      type="text"
                      name="url"
                      value={values.url}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.url && !!errors.url}
                      isValid={touched.url && !errors.url && !!values.url}
                    />
                    <Form.Control.Feedback type="valid">
                      Looks good!
                    </Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                      {errors.url}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Optional link to the full protocol document.
                    </Form.Text>
                  </Form.Group>

                  <ProductSearchMultiselect
                    token={token}
                    selectedProducts={relatedProducts}
                    setSelectedProducts={nextProducts => {
                      setRelatedProducts(nextProducts);
                      setProductsTouched(true);
                    }}
                    label="Linked products (CAT#)"
                  />
                </Modal.Body>

                <Modal.Footer className="procure-modal__footer">
                  {isSubmitting ? (
                    <Button
                      variant="primary"
                      className="glass-primary-btn"
                      disabled
                    >
                      <Spinner
                        size="sm"
                        as="span"
                        animation="border"
                        role="status"
                        aria-hidden="true"
                      />
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      className="glass-primary-btn"
                      disabled={
                        !isValid ||
                        (!isEditing && !dirty && relatedProducts.length === 0)
                      }
                      onClick={handleSubmit}
                    >
                      {isEditing ? 'Save' : 'Create'}
                    </Button>
                  )}

                  <Button
                    variant="secondary"
                    className="procure-modal__close-btn"
                    onClick={handleClose}
                  >
                    Close
                  </Button>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    </>
  );
};

export default ProtocolModal;
